import { FileZipOutlined, InboxOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Drawer,
  Modal,
  Progress,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import React, { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

import {
  type SalesMeeting,
  type SalesMeetingCallAnalysisFeedback,
} from "@fitness-app/data-models/entities/SalesMeeting";
import { type TeamMember } from "@fitness-app/data-models/entities/TeamMember";

import { UploadDndDropzone } from "~/components/Upload/UploadDndZone";
import { UploadFilesProvider, useUploadFilesContext } from "~/components/Upload/UploadProvider";
import {
  UploadFileStatus,
  type FileData,
  type StorageConfig,
  type UploadValidatorConfig,
} from "~/components/Upload/types";
import { backgroundTasksApi, supabase } from "~/store/initializeStore";

type SalesCallAnalysisMeeting = SalesMeeting & {
  teamMember?: Pick<TeamMember, "id" | "firstName" | "lastName" | "email" | "calendlyEmail"> | null;
};

type SalesCallAnalysisTabProps = {
  ownerId: string;
  sellers: TeamMember[];
  meetings: SalesCallAnalysisMeeting[];
  meetingsLoading: boolean;
};

type UploadedAnalysisFile = {
  bucket: string;
  path: string;
  url: string;
  originalFileName: string;
  size: number;
};

type UnmatchedRecordingAnalysis = {
  id: string;
  requestId: string | null;
  taskId: string | null;
  teamMemberId: string;
  teamMember?: Pick<TeamMember, "id" | "firstName" | "lastName" | "email" | "calendlyEmail"> | null;
  fileName: string;
  path: string;
  recordedAt: string | null;
  durationSeconds: number | null;
  transcript: string | null;
  transcriptSegments: unknown[] | null;
  analysis: SalesMeetingCallAnalysisFeedback | null;
  status: string;
  updatedAt: string;
};

type MeetingMatchOption = Pick<
  SalesMeeting,
  "id" | "startTime" | "endTime" | "clientName" | "clientEmail" | "sellerName" | "teamMemberId"
>;

const SALES_CALL_ANALYSIS_UPLOAD_BUCKET = "users";
const MAX_ZIP_SIZE_BYTES = 250 * 1024 * 1024;
const MAX_FILES_PER_BATCH = 100;
const allowedAnalysisFileExtensions = ["zip", "m4a", "mp3", "mp4", "wav", "webm", "ogg", "flac"];

const getUploadConfig = (
  objectPath: string,
): { validatorConfig: UploadValidatorConfig; storageConfig: StorageConfig } => ({
  validatorConfig: {
    allowedExtensions: allowedAnalysisFileExtensions,
    maxFileSize: MAX_ZIP_SIZE_BYTES,
    maxFilesListLength: MAX_FILES_PER_BATCH,
    maxFilesListSize: MAX_ZIP_SIZE_BYTES,
  },
  storageConfig: {
    bucketName: SALES_CALL_ANALYSIS_UPLOAD_BUCKET,
    isPublicBucket: false,
    objectPath,
  },
});

const callAnalysisStatusLabels: Record<string, string> = {
  queued: "W kolejce",
  skipped_short: "Za krótkie",
  transcribed: "Transkrypcja",
  matched: "Dopasowane",
  analyzed: "Przeanalizowane",
  unmatched: "Bez dopasowania",
  failed: "Błąd",
};

const callAnalysisStatusColor: Record<string, string> = {
  queued: "blue",
  skipped_short: "default",
  transcribed: "cyan",
  matched: "geekblue",
  analyzed: "green",
  unmatched: "gold",
  failed: "red",
};

const formatDurationSeconds = (seconds: number) => {
  const totalSeconds = Math.max(0, Math.round(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours} godz ${minutes.toString().padStart(2, "0")} min ${remainingSeconds.toString().padStart(2, "0")} s`;
  }

  if (minutes > 0) {
    return `${minutes} min ${remainingSeconds.toString().padStart(2, "0")} s`;
  }

  return `${remainingSeconds} s`;
};

const getUploadedAnalysisFile = (file: FileData, storageConfig: StorageConfig): UploadedAnalysisFile => ({
  bucket: storageConfig.bucketName,
  path: file.objectPath,
  url: file.url ?? "",
  originalFileName: file.originalName,
  size: file.size,
});

const SalesCallAnalysisFilesUploader = (): React.ReactElement => {
  const { files, removeFile } = useUploadFilesContext();

  if (!files.length) {
    return (
      <UploadDndDropzone clickable>
        <div className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center transition-colors hover:border-blue-400 hover:bg-blue-50">
          <InboxOutlined className="mb-3 text-4xl text-gray-400" />
          <Typography.Text className="block font-medium">Przeciągnij pliki albo kliknij</Typography.Text>
          <Typography.Text className="text-sm text-gray-500">
            Obsługiwane: pojedynczy plik audio, kilka plików audio albo ZIP.
          </Typography.Text>
        </div>
      </UploadDndDropzone>
    );
  }

  return (
    <div className="space-y-3">
      <UploadDndDropzone clickable>
        <Button icon={<InboxOutlined />} block>
          Dodaj kolejne pliki
        </Button>
      </UploadDndDropzone>

      <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3">
        {files.map((file) => {
          const isUploading = file.status === UploadFileStatus.UPLOADING;
          const isUploaded = file.status === UploadFileStatus.SUCCESS;
          const isError = file.status === UploadFileStatus.ERROR;

          return (
            <div key={file.uid} className="rounded-md border border-gray-100 bg-gray-50 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Typography.Text strong className="block truncate">
                    {file.originalName}
                  </Typography.Text>
                  {isUploaded ? (
                    <Typography.Text className="text-xs text-green-600">Upload zakończony</Typography.Text>
                  ) : null}
                  {isUploading ? (
                    <Typography.Text className="text-xs text-gray-500">Upload w toku</Typography.Text>
                  ) : null}
                  {isError ? (
                    <Typography.Text className="text-xs text-red-600">
                      {file.errorMessage ?? "Nie udało się wysłać pliku"}
                    </Typography.Text>
                  ) : null}
                </div>
                <Button size="small" danger onClick={() => removeFile(file)}>
                  Usuń
                </Button>
              </div>

              {isUploading ? <Progress className="mt-2" percent={file.progress ?? 0} status="active" /> : null}
              {isUploaded ? <Progress className="mt-2" percent={100} status="success" /> : null}
              {isError ? <Progress className="mt-2" percent={file.progress ?? 0} status="exception" /> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SalesCallAnalysisSubmitButton = ({
  uploadedFiles,
  analysisSellerId,
  isSubmitting,
  onSubmit,
}: {
  uploadedFiles: UploadedAnalysisFile[];
  analysisSellerId: string | null;
  isSubmitting: boolean;
  onSubmit: () => void;
}): React.ReactElement => {
  const { files } = useUploadFilesContext();
  const isUploading = files.some((file) => file.status === UploadFileStatus.UPLOADING);

  return (
    <Button
      type="primary"
      icon={<FileZipOutlined />}
      loading={isSubmitting}
      disabled={!uploadedFiles.length || !analysisSellerId || isUploading}
      onClick={onSubmit}
    >
      Uruchom analizę rozmów
    </Button>
  );
};

const FeedbackTextBlock = ({ title, value, accent }: { title: string; value?: string; accent?: "green" }) => {
  if (!value?.trim()) return null;

  return (
    <div
      className={
        accent === "green"
          ? "rounded-lg border border-emerald-100 bg-emerald-50 p-4"
          : "rounded-lg border border-gray-100 bg-gray-50 p-4"
      }
    >
      <Typography.Text
        className={
          accent === "green"
            ? "text-xs font-medium uppercase text-emerald-700"
            : "text-xs font-medium uppercase text-gray-500"
        }
      >
        {title}
      </Typography.Text>
      <Typography.Paragraph
        className={
          accent === "green"
            ? "!mb-0 !mt-2 whitespace-pre-wrap text-sm text-emerald-900"
            : "!mb-0 !mt-2 whitespace-pre-wrap text-sm text-gray-700"
        }
      >
        {value}
      </Typography.Paragraph>
    </div>
  );
};

const FeedbackListBlock = ({ title, items }: { title: string; items?: string[] }) => {
  if (!items?.length) return null;

  return (
    <div className="rounded-lg border border-gray-100 bg-white p-4">
      <Typography.Text className="text-xs font-medium uppercase text-gray-500">{title}</Typography.Text>
      <ul className="mb-0 mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

export const SalesCallAnalysisTab = ({
  ownerId,
  sellers,
  meetings,
  meetingsLoading,
}: SalesCallAnalysisTabProps): React.ReactElement => {
  const queryClient = useQueryClient();
  const [analysisSellerId, setAnalysisSellerId] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedAnalysisFile[]>([]);
  const [detailsMeeting, setDetailsMeeting] = useState<SalesCallAnalysisMeeting | null>(null);
  const [detailsRecording, setDetailsRecording] = useState<UnmatchedRecordingAnalysis | null>(null);
  const [recordingToMatch, setRecordingToMatch] = useState<UnmatchedRecordingAnalysis | null>(null);
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [meetingSearch, setMeetingSearch] = useState("");
  const [debouncedMeetingSearch] = useDebounce(meetingSearch, 300);
  const [uploadSessionId, setUploadSessionId] = useState(() => crypto.randomUUID());
  /**
   * TRE-1734 dołożyło po stronie API bramkę `isSalesRecordingSourcePath`, która wymaga
   * prefiksu `${ownerId}/sales-call-analysis/${teamMemberId}/`. Bez id sprzedawcy w ścieżce
   * każdy upload kończył się cichym 400 „Invalid file path".
   */
  const uploadObjectPath = useMemo(
    () => `${ownerId}/sales-call-analysis/${analysisSellerId}/${uploadSessionId}`,
    [ownerId, analysisSellerId, uploadSessionId],
  );
  const uploadConfig = useMemo(() => getUploadConfig(uploadObjectPath), [uploadObjectPath]);

  const sellerOptions = sellers.map((seller) => ({
    value: seller.id,
    label: `${seller.firstName} ${seller.lastName}`,
  }));

  const analyzedMeetings = useMemo(
    () => meetings.filter((meeting) => meeting.callAnalysisStatus || meeting.callTranscript).slice(0, 20),
    [meetings],
  );
  const detailsDurationSeconds = detailsMeeting?.callRecordingDurationSeconds ?? null;

  const unmatchedRecordingsQuery = useQuery({
    queryKey: ["salesCallUnmatchedRecordings", ownerId],
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sales_call_recording_analysis")
        .select(
          "id, requestId, taskId, teamMemberId, teamMember:teamMemberId(id, firstName, lastName, email, calendlyEmail), fileName, path, recordedAt, durationSeconds, transcript, transcriptSegments, analysis, status, updatedAt",
        )
        .eq("ownerId", ownerId)
        .eq("status", "unmatched")
        .is("matchedSalesMeetingId", null)
        .not("analysis", "is", null)
        .order("updatedAt", { ascending: false })
        .limit(50);

      if (error) throw new Error(error.message);

      return (data ?? []) as unknown as UnmatchedRecordingAnalysis[];
    },
  });

  const meetingMatchOptionsQuery = useQuery({
    queryKey: [
      "salesCallMeetingMatchOptions",
      recordingToMatch?.id,
      recordingToMatch?.recordedAt,
      debouncedMeetingSearch,
    ],
    enabled: Boolean(recordingToMatch),
    retry: false,
    queryFn: async () => {
      const search = debouncedMeetingSearch.replace(/[%_,]/g, " ").trim();
      let query = supabase
        .from("sales_meeting")
        .select("id, startTime, endTime, clientName, clientEmail, sellerName, teamMemberId")
        .order("startTime", { ascending: false })
        .limit(50);

      if (search) {
        query = query.or(`clientEmail.ilike.%${search}%,clientName.ilike.%${search}%,sellerName.ilike.%${search}%`);
      } else if (recordingToMatch?.recordedAt) {
        const recordedAt = dayjs(recordingToMatch.recordedAt);

        if (recordedAt.isValid()) {
          query = query
            .gte("startTime", recordedAt.subtract(14, "day").toISOString())
            .lte("startTime", recordedAt.add(7, "day").toISOString());
        }
      }

      const { data, error } = await query;

      if (error) throw new Error(error.message);
      return (data ?? []) as MeetingMatchOption[];
    },
  });

  const handleUploadSuccess = (file: FileData) => {
    const uploadedFile = getUploadedAnalysisFile(file, uploadConfig.storageConfig);
    setUploadedFiles((prev) => [...prev.filter((item) => item.path !== uploadedFile.path), uploadedFile]);
  };

  /** Pliki leżą pod ścieżką konkretnego sprzedawcy, więc zmiana sprzedawcy unieważnia batch. */
  const handleSellerChange = (value: string | null | undefined) => {
    setAnalysisSellerId(value ?? null);
    setUploadedFiles([]);
    setUploadSessionId(crypto.randomUUID());
  };

  const handleUploadRemove = (file: FileData) => {
    const removedFile = getUploadedAnalysisFile(file, uploadConfig.storageConfig);
    setUploadedFiles((prev) => prev.filter((item) => item.path !== removedFile.path));
  };

  const analyzeCallsMutation = useMutation({
    mutationFn: async () => {
      if (!analysisSellerId) throw new Error("Wybierz sprzedawcę");
      if (!uploadedFiles.length) throw new Error("Najpierw wyślij nagrania albo zip");

      const response = await backgroundTasksApi.post(
        "/api/sales-agent/analyze-calls",
        {
          files: uploadedFiles,
        },
        {
          params: { teamMemberId: analysisSellerId },
        },
      );

      return response.data as {
        status: "success" | "skipped";
        requestId: string | null;
        recordings: number;
        skippedEntries: number;
        skippedAlreadyProcessed: number;
      };
    },
    onSuccess: async (data) => {
      setUploadedFiles([]);
      setUploadSessionId(crypto.randomUUID());
      await queryClient.invalidateQueries({ queryKey: ["salesCrmMeetings"] });
      await queryClient.invalidateQueries({ queryKey: ["ai_agent_request"] });
      await queryClient.invalidateQueries({ queryKey: ["salesCallUnmatchedRecordings"] });

      if (data.recordings === 0 && data.skippedAlreadyProcessed > 0) {
        void message.info(`Wszystkie pliki były już przetworzone: ${data.skippedAlreadyProcessed}`);
        return;
      }

      void message.success(
        `Analiza uruchomiona: ${data.recordings} nagrań, ${data.skippedEntries} plików pominiętych, ${data.skippedAlreadyProcessed} już przetworzonych`,
      );
    },
    onError: (error) => {
      void message.error(error instanceof Error ? error.message : "Nie udało się uruchomić analizy rozmów");
    },
  });

  const matchRecordingMutation = useMutation({
    mutationFn: async () => {
      if (!recordingToMatch) throw new Error("Wybierz nagranie");
      if (!selectedMeetingId) throw new Error("Wybierz spotkanie");
      if (!recordingToMatch.analysis) throw new Error("Nagranie nie ma zapisanej analizy");

      const { error: meetingError } = await supabase
        .from("sales_meeting")
        .update({
          callAnalysisStatus: "analyzed",
          callAnalysisScore: Math.round(recordingToMatch.analysis.score),
          callAnalysisFeedback: recordingToMatch.analysis,
          callAnalysisAt: new Date().toISOString(),
          callAnalysisRequestId: recordingToMatch.requestId,
          callTranscript: recordingToMatch.transcript,
          callTranscriptSegments: recordingToMatch.transcriptSegments,
          callRecordingDurationSeconds: recordingToMatch.durationSeconds,
          callRecordingName: recordingToMatch.fileName,
          callRecordingPath: recordingToMatch.path,
          callRecordingStartedAt: recordingToMatch.recordedAt,
          updatedAt: new Date().toISOString(),
        } as never)
        .eq("id", selectedMeetingId);

      if (meetingError) throw new Error(meetingError.message);

      const { error: recordingError } = await supabase
        .from("sales_call_recording_analysis")
        .update({
          status: "analyzed",
          matchedSalesMeetingId: selectedMeetingId,
          updatedAt: new Date().toISOString(),
        })
        .eq("id", recordingToMatch.id);

      if (recordingError) throw new Error(recordingError.message);
    },
    onSuccess: async () => {
      setRecordingToMatch(null);
      setSelectedMeetingId(null);
      setMeetingSearch("");
      await queryClient.invalidateQueries({ queryKey: ["salesCrmMeetings"] });
      await queryClient.invalidateQueries({ queryKey: ["salesCallUnmatchedRecordings"] });
      void message.success("Nagranie dopasowane do spotkania");
    },
    onError: (error) => {
      void message.error(error instanceof Error ? error.message : "Nie udało się dopasować nagrania");
    },
  });

  const openMatchModal = (recording: UnmatchedRecordingAnalysis) => {
    setRecordingToMatch(recording);
    setSelectedMeetingId(null);
    setMeetingSearch("");
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[0.9fr_1.1fr]">
        <Card styles={{ body: { padding: 20 } }} className="rounded-lg border border-gray-200 shadow-sm">
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <div>
              <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Batch transkrypcji
              </Typography.Text>
              <Typography.Title level={4} className="!mb-0 !mt-1 !text-lg !font-semibold">
                Nagrania rozmów
              </Typography.Title>
            </div>

            <Select
              placeholder="Sprzedawca"
              value={analysisSellerId}
              onChange={handleSellerChange}
              options={sellerOptions}
              style={{ width: "100%" }}
            />

            {analysisSellerId ? (
              <UploadFilesProvider
                key={`${analysisSellerId}-${uploadSessionId}`}
                {...uploadConfig}
                onSuccess={handleUploadSuccess}
                onRemove={handleUploadRemove}
                onError={handleUploadRemove}
              >
                <>
                  <SalesCallAnalysisFilesUploader />
                  <div className="flex justify-end pt-2">
                    <SalesCallAnalysisSubmitButton
                      uploadedFiles={uploadedFiles}
                      analysisSellerId={analysisSellerId}
                      isSubmitting={analyzeCallsMutation.isLoading}
                      onSubmit={() => analyzeCallsMutation.mutate()}
                    />
                  </div>
                </>
              </UploadFilesProvider>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center">
                <Typography.Text className="text-sm text-gray-500">
                  Wybierz sprzedawcę, żeby dodać nagrania.
                </Typography.Text>
              </div>
            )}
          </Space>
        </Card>

        <Card styles={{ body: { padding: 20 } }} className="rounded-lg border border-gray-200 shadow-sm">
          <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Jak działa dopasowanie
          </Typography.Text>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <Typography.Text className="font-medium">1. Dodaj nagrania</Typography.Text>
              <Typography.Text className="mt-1 block text-sm text-gray-500">
                Możesz dodać pojedyncze MP3, kilka plików audio albo ZIP z nagraniami.
              </Typography.Text>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <Typography.Text className="font-medium">2. Transkrypcja</Typography.Text>
              <Typography.Text className="mt-1 block text-sm text-gray-500">
                Task odrzuca pliki krótsze niż 30 sekund i używa ElevenLabs Scribe v2.
              </Typography.Text>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <Typography.Text className="font-medium">3. Feedback</Typography.Text>
              <Typography.Text className="mt-1 block text-sm text-gray-500">
                Timestamp nagrania jest dopasowany do sales_meeting, a Sales Agent zapisuje ocenę.
              </Typography.Text>
            </div>
          </div>
        </Card>
      </div>

      {sellers.length ? null : (
        <Alert
          showIcon
          type="warning"
          message="Brak sprzedawców do wyboru"
          description="Najpierw dodaj albo zsynchronizuj sprzedawców z Calendly."
        />
      )}

      <Table<SalesCallAnalysisMeeting>
        rowKey="id"
        dataSource={analyzedMeetings}
        loading={meetingsLoading}
        scroll={{ x: true }}
      >
        <Table.Column<SalesCallAnalysisMeeting>
          title="Spotkanie"
          render={(_, row) => (
            <Space direction="vertical" size={0}>
              <Typography.Text>{row.clientName ?? row.clientEmail}</Typography.Text>
              <Typography.Text type="secondary">{dayjs(row.startTime).format("DD.MM.YYYY HH:mm")}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<SalesCallAnalysisMeeting>
          title="Sprzedawca"
          render={(_, row) =>
            row.teamMember ? `${row.teamMember.firstName} ${row.teamMember.lastName}` : (row.sellerName ?? "-")
          }
        />
        <Table.Column<SalesCallAnalysisMeeting>
          title="Nagranie"
          dataIndex="callRecordingName"
          ellipsis
          className="max-w-[220px]"
          render={(value) => value ?? "-"}
        />
        <Table.Column<SalesCallAnalysisMeeting>
          title="Czas"
          render={(_, row) => {
            const durationSeconds = row.callRecordingDurationSeconds;
            return typeof durationSeconds === "number" ? formatDurationSeconds(durationSeconds) : "-";
          }}
          className="whitespace-nowrap"
        />
        <Table.Column<SalesCallAnalysisMeeting>
          title="Status"
          dataIndex="callAnalysisStatus"
          render={(value: string | null) =>
            value ? (
              <Tag color={callAnalysisStatusColor[value] ?? "default"}>{callAnalysisStatusLabels[value] ?? value}</Tag>
            ) : (
              "-"
            )
          }
        />
        <Table.Column<SalesCallAnalysisMeeting>
          title="Ocena"
          dataIndex="callAnalysisScore"
          render={(value: number | null) => (typeof value === "number" ? `${value}/100` : "-")}
        />
        <Table.Column<SalesCallAnalysisMeeting>
          title="Feedback"
          render={(_, row) => row.callAnalysisFeedback?.summary ?? "-"}
        />
        <Table.Column<SalesCallAnalysisMeeting>
          title=""
          render={(_, row) => (
            <Button
              size="small"
              disabled={!row.callAnalysisFeedback && !row.callTranscript}
              onClick={() => setDetailsMeeting(row)}
            >
              Szczegóły
            </Button>
          )}
        />
      </Table>

      <Card styles={{ body: { padding: 20 } }} className="rounded-lg border border-gray-200 shadow-sm">
        <div className="mb-4">
          <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Niedopasowane nagrania
          </Typography.Text>
          <Typography.Title level={4} className="!mb-0 !mt-1 !text-lg !font-semibold">
            Analizy bez spotkania Calendly
          </Typography.Title>
        </div>

        <Table<UnmatchedRecordingAnalysis>
          rowKey="id"
          dataSource={unmatchedRecordingsQuery.data ?? []}
          loading={unmatchedRecordingsQuery.isLoading}
          scroll={{ x: true }}
        >
          <Table.Column<UnmatchedRecordingAnalysis>
            title="Nagranie"
            ellipsis
            className="max-w-[260px]"
            render={(_, row) => (
              <Space direction="vertical" size={0}>
                <Typography.Text>{row.fileName}</Typography.Text>
                {row.recordedAt ? (
                  <Typography.Text type="secondary">{dayjs(row.recordedAt).format("DD.MM.YYYY HH:mm")}</Typography.Text>
                ) : null}
              </Space>
            )}
          />
          <Table.Column<UnmatchedRecordingAnalysis>
            title="Sprzedawca"
            render={(_, row) =>
              row.teamMember ? `${row.teamMember.firstName} ${row.teamMember.lastName}` : row.teamMemberId
            }
          />
          <Table.Column<UnmatchedRecordingAnalysis>
            title="Czas"
            className="whitespace-nowrap"
            render={(_, row) =>
              typeof row.durationSeconds === "number" ? formatDurationSeconds(row.durationSeconds) : "-"
            }
          />
          <Table.Column<UnmatchedRecordingAnalysis>
            title="Status"
            render={(_, row) => (
              <Tag color={callAnalysisStatusColor[row.status] ?? "default"}>
                {callAnalysisStatusLabels[row.status] ?? row.status}
              </Tag>
            )}
          />
          <Table.Column<UnmatchedRecordingAnalysis>
            title="Ocena"
            render={(_, row) =>
              typeof row.analysis?.score === "number" ? `${Math.round(row.analysis.score)}/100` : "-"
            }
          />
          <Table.Column<UnmatchedRecordingAnalysis>
            title="Feedback"
            render={(_, row) => row.analysis?.summary ?? "-"}
          />
          <Table.Column<UnmatchedRecordingAnalysis>
            title=""
            render={(_, row) => (
              <Space>
                <Button
                  size="small"
                  disabled={!row.analysis && !row.transcript}
                  onClick={() => setDetailsRecording(row)}
                >
                  Szczegóły
                </Button>
                <Button size="small" type="primary" disabled={!row.analysis} onClick={() => openMatchModal(row)}>
                  Dopasuj
                </Button>
              </Space>
            )}
          />
        </Table>
      </Card>

      <Modal
        title="Dopasuj nagranie do spotkania"
        open={Boolean(recordingToMatch)}
        onCancel={() => {
          setRecordingToMatch(null);
          setSelectedMeetingId(null);
          setMeetingSearch("");
        }}
        onOk={() => matchRecordingMutation.mutate()}
        okText="Dopasuj"
        confirmLoading={matchRecordingMutation.isLoading}
        okButtonProps={{ disabled: !selectedMeetingId }}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {recordingToMatch ? (
            <Typography.Text type="secondary">
              {recordingToMatch.fileName}
              {recordingToMatch.recordedAt ? ` · ${dayjs(recordingToMatch.recordedAt).format("DD.MM.YYYY HH:mm")}` : ""}
            </Typography.Text>
          ) : null}
          <Select
            allowClear
            showSearch
            filterOption={false}
            value={selectedMeetingId}
            loading={meetingMatchOptionsQuery.isLoading}
            onSearch={setMeetingSearch}
            onChange={(value) => setSelectedMeetingId(value ?? null)}
            placeholder="Wyszukaj spotkanie po mailu, nazwie lub sprzedawcy"
            notFoundContent={meetingMatchOptionsQuery.isLoading ? "Szukam..." : "Brak spotkań"}
            style={{ width: "100%" }}
            options={(meetingMatchOptionsQuery.data ?? []).map((meeting) => ({
              value: meeting.id,
              label: `${meeting.clientEmail} · ${meeting.clientName ?? "bez nazwy"} · ${dayjs(meeting.startTime).format(
                "DD.MM.YYYY HH:mm",
              )} · ${meeting.sellerName ?? "-"}`,
            }))}
          />
        </Space>
      </Modal>

      <Drawer
        open={Boolean(detailsMeeting)}
        title="Szczegóły analizy rozmowy"
        width={720}
        onClose={() => setDetailsMeeting(null)}
      >
        {detailsMeeting ? (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <div>
              <Typography.Text className="text-xs font-medium uppercase text-gray-500">Spotkanie</Typography.Text>
              <Typography.Title level={4} className="!mb-0 !mt-1 !text-lg">
                {detailsMeeting.clientName ?? detailsMeeting.clientEmail}
              </Typography.Title>
              <Typography.Text type="secondary">
                {dayjs(detailsMeeting.startTime).format("DD.MM.YYYY HH:mm")}
                {detailsMeeting.callRecordingName ? ` · ${detailsMeeting.callRecordingName}` : ""}
              </Typography.Text>
            </div>

            {typeof detailsMeeting.callAnalysisScore === "number" || detailsDurationSeconds !== null ? (
              <div className="flex flex-wrap gap-2">
                {typeof detailsMeeting.callAnalysisScore === "number" ? (
                  <Tag color="green" className="m-0">
                    Ocena {detailsMeeting.callAnalysisScore}/100
                  </Tag>
                ) : null}
                {detailsDurationSeconds !== null ? (
                  <Tag color="blue" className="m-0">
                    Długość {formatDurationSeconds(detailsDurationSeconds)}
                  </Tag>
                ) : null}
              </div>
            ) : null}

            {detailsMeeting.callTranscript?.trim() ? (
              <Collapse
                bordered={false}
                className="rounded-lg border border-gray-100 bg-white"
                items={[
                  {
                    key: "transcript",
                    label: <Typography.Text className="font-medium">Transkrypcja</Typography.Text>,
                    children: (
                      <Typography.Paragraph className="!mb-0 whitespace-pre-wrap text-sm text-gray-700">
                        {detailsMeeting.callTranscript}
                      </Typography.Paragraph>
                    ),
                  },
                ]}
              />
            ) : null}

            <FeedbackTextBlock title="Podsumowanie" value={detailsMeeting.callAnalysisFeedback?.summary} />
            <FeedbackTextBlock
              title="Ocena szczegółowa"
              value={detailsMeeting.callAnalysisFeedback?.detailedAssessment}
            />
            <FeedbackTextBlock
              title="Feedback do przekazania"
              value={detailsMeeting.callAnalysisFeedback?.teamMemberFeedback}
              accent="green"
            />
            <FeedbackListBlock title="Mocne strony" items={detailsMeeting.callAnalysisFeedback?.strengths} />
            <FeedbackListBlock title="Ryzyka i straty" items={detailsMeeting.callAnalysisFeedback?.risks} />
            <FeedbackListBlock title="Rekomendacje" items={detailsMeeting.callAnalysisFeedback?.recommendations} />
            <FeedbackListBlock title="Brakujące pytania" items={detailsMeeting.callAnalysisFeedback?.missedQuestions} />
            <FeedbackTextBlock
              title="Najbliższy krok coachingowy"
              value={detailsMeeting.callAnalysisFeedback?.nextCoachingStep}
            />
          </Space>
        ) : null}
      </Drawer>

      <Drawer
        open={Boolean(detailsRecording)}
        title="Szczegóły niedopasowanego nagrania"
        width={720}
        onClose={() => setDetailsRecording(null)}
      >
        {detailsRecording ? (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <div>
              <Typography.Text className="text-xs font-medium uppercase text-gray-500">Nagranie</Typography.Text>
              <Typography.Title level={4} className="!mb-0 !mt-1 !text-lg">
                {detailsRecording.fileName}
              </Typography.Title>
              <Typography.Text type="secondary">
                {detailsRecording.recordedAt
                  ? dayjs(detailsRecording.recordedAt).format("DD.MM.YYYY HH:mm")
                  : "Brak daty"}
              </Typography.Text>
            </div>

            {typeof detailsRecording.analysis?.score === "number" || detailsRecording.durationSeconds !== null ? (
              <div className="flex flex-wrap gap-2">
                {typeof detailsRecording.analysis?.score === "number" ? (
                  <Tag color="green" className="m-0">
                    Ocena {Math.round(detailsRecording.analysis.score)}/100
                  </Tag>
                ) : null}
                {detailsRecording.durationSeconds !== null ? (
                  <Tag color="blue" className="m-0">
                    Długość {formatDurationSeconds(detailsRecording.durationSeconds)}
                  </Tag>
                ) : null}
              </div>
            ) : null}

            {detailsRecording.transcript?.trim() ? (
              <Collapse
                bordered={false}
                className="rounded-lg border border-gray-100 bg-white"
                items={[
                  {
                    key: "transcript",
                    label: <Typography.Text className="font-medium">Transkrypcja</Typography.Text>,
                    children: (
                      <Typography.Paragraph className="!mb-0 whitespace-pre-wrap text-sm text-gray-700">
                        {detailsRecording.transcript}
                      </Typography.Paragraph>
                    ),
                  },
                ]}
              />
            ) : null}

            <FeedbackTextBlock title="Podsumowanie" value={detailsRecording.analysis?.summary} />
            <FeedbackTextBlock title="Ocena szczegółowa" value={detailsRecording.analysis?.detailedAssessment} />
            <FeedbackTextBlock
              title="Feedback do przekazania"
              value={detailsRecording.analysis?.teamMemberFeedback}
              accent="green"
            />
            <FeedbackListBlock title="Mocne strony" items={detailsRecording.analysis?.strengths} />
            <FeedbackListBlock title="Ryzyka i straty" items={detailsRecording.analysis?.risks} />
            <FeedbackListBlock title="Rekomendacje" items={detailsRecording.analysis?.recommendations} />
            <FeedbackListBlock title="Brakujące pytania" items={detailsRecording.analysis?.missedQuestions} />
            <FeedbackTextBlock
              title="Najbliższy krok coachingowy"
              value={detailsRecording.analysis?.nextCoachingStep}
            />
          </Space>
        ) : null}
      </Drawer>
    </Space>
  );
};
